import Link from '@mui/material/Link';
import imageCompression from 'browser-image-compression';
import { Attachment } from 'fhir/r4b';
import { DateTime } from 'luxon';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { addContentTypeToAttachment } from 'utils/lib/helpers/paperwork/files-docs';
import {
  INSURANCE_CARD_FRONT_2_ID,
  INSURANCE_CARD_FRONT_ID,
  PHOTO_ID_FRONT_ID,
} from 'utils/lib/types/data/paperwork/paperwork.constants';
import { convertHeicToJpegIfNeeded } from '../../../../utils/heic';
import { PaperworkContext } from '../../context';
import CardDisplay from './CardDisplay';
import NonImageCardComponent from './NonImageCardComponent';
import UploadComponent from './UploadComponent';

export type AttachmentType = 'image' | 'pdf';

export const COMPRESS_THRESHOLD_MB = 1;
export const COMPRESS_TARGET_MB = 0.9;

interface FileInputProps {
  fieldName: string;
  fileName: string;
  value: Attachment | undefined;
  description: string;
  attachmentType?: AttachmentType;
  onChange: (event: any) => void;
  usePaperworkContext: () => PaperworkContext;
}

enum UploadState {
  initial,
  pending,
  complete,
  failed,
}

const FileInput: FC<FileInputProps> = ({
  fieldName: name,
  fileName,
  value,
  onChange,
  description,
  attachmentType = 'image',
  usePaperworkContext,
}) => {
  const { formState } = useFormContext();
  const { defaultValues } = formState;
  const { appointment, setSaveButtonDisabled, paperworkComponentHelpers } = usePaperworkContext();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingZ3Upload, setPendingZ3Upload] = useState<File | undefined>();
  const [z3UploadState, setZ3UploadState] = useState(UploadState.initial);
  const [compressingImage, setCompressingImage] = useState(false);
  const { createZ3Object, getInsuranceCardSuggestions, getPhotoIdSuggestions } = paperworkComponentHelpers;
  // when the user clears an existing value, then uploads a different image, then deletes it, we set the value
  // back to the starting value in the form state since we won't actually be deleting the existing image in that case.
  // this prevents the upload button from appearing, because value exists. this additional state is a hack to make the upload button
  // show up in that case as well.
  const [existingValueCleared, setExistingValueCleared] = useState(false);

  const defaultVal = (() => {
    const pathParts = name.split('.');
    const baseField = pathParts.reduce((accum, current) => {
      return accum?.[current];
    }, defaultValues);
    return baseField?.answer?.[0]?.valueAttachment;
  })();

  const inputRef = useRef<HTMLInputElement>(null);
  const handleReuploadClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    try {
      if (inputRef?.current) {
        inputRef.current.click();
      } else {
        throw new Error('inputRef is not defined');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleClearSelection = useCallback(async () => {
    setZ3UploadState(UploadState.initial);
    setPreviewUrl(null);
    setExistingValueCleared(true);
    if (attachmentType === 'pdf') {
      // for pdfs, we don't offer the 're-upload' option, so we just clear the value and that will allow ths user to re-upload
      // this could result in an image showing up in the EHR that the user believes they "removed", but we can address that later if necessary
      onChange(null);
    } else {
      onChange(defaultVal);
    }
  }, [attachmentType, defaultVal, onChange]);

  const uploadDescription = useMemo(() => {
    const defaultVal = value; //((defaultValues ?? {})[name]);

    if (defaultVal?.url && !previewUrl) {
      const creationDate = defaultVal?.creation ? DateTime.fromISO(defaultVal.creation) : null;
      let dateString = '';
      if (creationDate) {
        dateString = `${creationDate.toLocaleString({ dateStyle: 'short' })}. `;
      }
      return (
        <>
          {`We already have this! It was saved on ${dateString}`}
          <Link href="#" onClick={handleReuploadClick} underline="hover">
            {'Click to re-upload'}
          </Link>
          .
        </>
      );
    }
    return description;
  }, [value, previewUrl, description]);

  useEffect(() => {
    const saveObjectToZ3 = async (file: File, appointmentId: string): Promise<void> => {
      if (!createZ3Object) return;

      try {
        setZ3UploadState(UploadState.pending);
        const createZ3Input = {
          appointmentID: appointmentId,
          fileType: fileName, // im surprised this is working based on what is defined for the types
          fileFormat: file.type.split('/')[1],
          file: file,
        };
        const uploadResponse = await createZ3Object(createZ3Input); // fileDate is just of type File
        const { z3URL } = uploadResponse;
        const attachment: Attachment = {
          url: z3URL,
          title: fileName,
          creation: DateTime.now().toISO(),
        };
        const enrichedAttachment = addContentTypeToAttachment(attachment);
        onChange(enrichedAttachment);
        setZ3UploadState(UploadState.complete);
        setPendingZ3Upload(undefined);
        setSaveButtonDisabled(false);

        // Suggestions are a nice-to-have on top of the upload, not part of its success/failure —
        // only the front image carries the fields we currently have a use for (see
        // extract-insurance-card/extract-photo-id: back images aren't OCR'd for photo ID either).
        const suggestionsInput = {
          appointmentID: appointmentId,
          fileURL: z3URL,
          fileContentType: enrichedAttachment.contentType,
          cardSlot: fileName,
        };
        if (fileName === INSURANCE_CARD_FRONT_ID || fileName === INSURANCE_CARD_FRONT_2_ID) {
          getInsuranceCardSuggestions?.(suggestionsInput).catch((error) =>
            console.error('Failed to fetch insurance card suggestions:', error)
          );
        } else if (fileName === PHOTO_ID_FRONT_ID) {
          getPhotoIdSuggestions?.(suggestionsInput).catch((error) =>
            console.error('Failed to fetch photo ID suggestions:', error)
          );
        }
      } catch (e) {
        console.error(e);
        setZ3UploadState(UploadState.failed);
      } finally {
        setSaveButtonDisabled(false);
      }
    };
    if (pendingZ3Upload && appointment?.id && z3UploadState === UploadState.initial) {
      void saveObjectToZ3(pendingZ3Upload, appointment.id);
    }
  }, [
    pendingZ3Upload,
    name,
    appointment?.id,
    z3UploadState,
    fileName,
    onChange,
    setSaveButtonDisabled,
    createZ3Object,
    getInsuranceCardSuggestions,
    getPhotoIdSuggestions,
  ]);

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>): Promise<string | null> => {
    try {
      const { files } = event.target;

      if (files && files.length > 0) {
        // Even though files is an array we know there is always only one file because we don't set the `multiple` attribute on the file input
        const rawFile = files[0];
        const file = attachmentType === 'image' ? await convertHeicToJpegIfNeeded(rawFile) : rawFile;
        let finalFile = file;
        setSaveButtonDisabled(true);
        if (attachmentType === 'image') {
          const fileSizeInMb = file.size / (1024 * 1024);
          if (fileSizeInMb >= COMPRESS_THRESHOLD_MB) {
            setCompressingImage(true);
            try {
              finalFile = await imageCompression(file, { maxSizeMB: COMPRESS_TARGET_MB });
            } catch (error) {
              console.error('error compressing file', error);
            }
          }
        }

        const tempURL = URL.createObjectURL(finalFile);
        setPreviewUrl(tempURL); // Use this as a temporary image URL until the insurance info form is submitted
        setPendingZ3Upload(finalFile);
        setZ3UploadState(UploadState.initial);
        setCompressingImage(false);
        return finalFile.name;
      } else {
        console.log('No files selected');
        return null;
      }
    } catch (error) {
      console.error('Error occurred during file upload:', error);
      setCompressingImage(false);
      setSaveButtonDisabled(false);
      return null;
    }
  };

  const showCard = (): JSX.Element => {
    // Prompt to re-upload file if upload fails
    if (z3UploadState === UploadState.failed) {
      return (
        <UploadComponent
          name={name}
          attachmentType={attachmentType}
          uploadDescription="Failed to upload card. Please try again"
          isCompressing={false}
          handleFileUpload={handleClearSelection}
          inputRef={inputRef}
          showUploadButton={true}
        />
      );
    }

    if (previewUrl) {
      if (attachmentType === 'image') {
        return (
          <CardDisplay
            name={name}
            previewUrl={previewUrl}
            isLoading={z3UploadState === UploadState.pending}
            onClear={handleClearSelection}
          />
        );
      } else {
        return (
          <NonImageCardComponent
            fileName={`${value?.title ?? fileName ?? 'note_template'}.pdf`}
            fileUrl={previewUrl}
            onClear={handleClearSelection}
          />
        );
      }
    }
    // Otherwise prompt to upload a file
    if (attachmentType === 'pdf' && value) {
      return (
        <NonImageCardComponent
          fileName={`${value?.title ?? fileName ?? 'note_template'}.pdf`}
          fileUrl={value?.url}
          onClear={handleClearSelection}
        />
      );
    }
    return (
      <UploadComponent
        name={name}
        attachmentType={attachmentType}
        uploadDescription={uploadDescription}
        handleFileUpload={handleFileUpload}
        inputRef={inputRef}
        showUploadButton={!value || existingValueCleared}
        isCompressing={compressingImage}
      />
    );
  };

  return showCard();
};

export default FileInput;
